import { NavLink, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  LayoutDashboard,
  Users,
  CreditCard,
  ArrowUpRight,
  ArrowDownLeft,
  BarChart3,
  Activity,
  MessageSquare,
  X,
  LogOut
} from 'lucide-react'
import { partnerApiService } from '@/services/partnerApiService'
import { toast } from 'react-hot-toast'

interface PartnerSidebarProps {
  isOpen: boolean
  onClose: () => void
  partner: {
    name: string
    company?: string
    can_view_users: boolean
    can_view_trading: boolean
    can_view_deposits: boolean
    can_view_withdrawals: boolean
    can_view_fees: boolean
    can_view_statistics: boolean
    can_view_realtime?: boolean
  }
}

const PartnerSidebar = ({ isOpen, onClose, partner }: PartnerSidebarProps) => {
  const { t } = useTranslation('sidebar')
  const navigate = useNavigate()

  const menuItems = [
    {
      title: t('dashboard'),
      icon: LayoutDashboard,
      path: '/partner',
      color: 'text-primary-400',
      show: true
    },
    {
      title: t('users'),
      icon: Users,
      path: '/partner/users',
      color: 'text-blue-400',
      show: partner.can_view_users
    },
    {
      title: t('trading'),
      icon: BarChart3,
      path: '/partner/trading',
      color: 'text-green-400',
      show: partner.can_view_trading
    },
    {
      title: t('statistics', '통계'),
      icon: BarChart3,
      path: '/partner/statistics',
      color: 'text-cyan-400',
      show: partner.can_view_statistics
    },
    {
      title: t('realtime', '실시간 현황'),
      icon: Activity,
      path: '/partner/realtime',
      color: 'text-emerald-400',
      show: !!partner.can_view_realtime
    },
    {
      title: t('deposits'),
      icon: CreditCard,
      path: '/partner/deposits',
      color: 'text-yellow-400',
      show: partner.can_view_deposits
    },
    {
      title: t('withdrawals'),
      icon: ArrowUpRight,
      path: '/partner/withdrawals',
      color: 'text-red-400',
      show: partner.can_view_withdrawals
    },
    {
      title: t('myWithdrawals'),
      icon: ArrowDownLeft,
      path: '/partner/my-withdrawals',
      color: 'text-orange-400',
      show: true
    },
    {
      title: t('inquiries'),
      icon: MessageSquare,
      path: '/partner/inquiries',
      color: 'text-pink-400',
      show: true
    },
  ].filter(item => item.show)

  const handleLogout = async () => {
    try {
      await partnerApiService.logout()
      localStorage.clear()
      sessionStorage.clear()

      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      })

      toast.success(t('logoutSuccess'))
      window.location.href = '/login'
    } catch (error) {
      console.error('Logout error:', error)
      localStorage.clear()
      sessionStorage.clear()

      document.cookie.split(";").forEach((c) => {
        document.cookie = c
          .replace(/^ +/, "")
          .replace(/=.*/, "=;expires=" + new Date().toUTCString() + ";path=/");
      })

      window.location.href = '/login'
    }
  }

  return (
    <aside className="h-full w-full glass border-r border-white/10 flex flex-col rounded-none relative" data-open={isOpen}>
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-white/10 flex-shrink-0">
        <div className="flex items-center space-x-2">
          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
            <span className="text-white font-bold text-sm">E</span>
          </div>
          <div>
            <h1 className="text-sm font-bold text-white">KRYZONIC</h1>
            <p className="text-[10px] text-dark-400">{t('partnerPortal')}</p>
          </div>
        </div>

        {/* Mobile close button */}
        <button
          onClick={onClose}
          className="lg:hidden glass-button p-2 rounded-lg"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-2 space-y-1 overflow-y-auto min-h-0">
        {menuItems.map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            end={item.path === '/partner'}
            className={({ isActive }) =>
              `flex items-center space-x-2 px-3 py-2.5 rounded-lg transition-all duration-200 group ${
                isActive
                  ? 'bg-white/10 text-white shadow-glass-sm'
                  : 'text-dark-300 hover:bg-white/5 hover:text-white'
              }`
            }
            onClick={() => {
              if (window.innerWidth < 1024) {
                onClose()
              }
            }}
          >
            {({ isActive }) => (
              <>
                <div className={`p-1.5 rounded-lg transition-all duration-200 ${
                  isActive
                    ? 'bg-white/10 shadow-glass-sm'
                    : 'group-hover:bg-white/5'
                }`}>
                  <item.icon
                    className={`w-4 h-4 transition-colors duration-200 ${
                      isActive ? item.color : 'text-dark-400 group-hover:text-dark-200'
                    }`}
                  />
                </div>
                <span className="text-sm font-medium">
                  {item.title}
                </span>

                {/* Active indicator */}
                {isActive && (
                  <div className="ml-auto w-1.5 h-1.5 bg-primary-500 rounded-full" />
                )}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Logout Button - Fixed at bottom */}
      <div className="p-2 border-t border-white/10 flex-shrink-0">
        <button
          onClick={handleLogout}
          className="w-full flex items-center space-x-2 px-3 py-2.5 rounded-lg transition-all duration-200 group text-dark-300 hover:bg-red-500/10 hover:text-red-400"
        >
          <div className="p-1.5 rounded-lg group-hover:bg-red-500/10">
            <LogOut className="w-4 h-4" />
          </div>
          <span className="text-sm font-medium">{t('logout')}</span>
        </button>
      </div>
    </aside>
  )
}

export default PartnerSidebar